import { AxiosResponse } from "axios";
import { CacheAxiosResponse } from "axios-cache-interceptor";

import { axios, axiosStationery } from "../api";

import type {
  StationeryCreate,
  StationeryFields,
  StationeryHelpLink,
  StationeryHelpLinkCreate,
  StationeryHelpLinkUpdate,
  StationeryModule,
  StationeryRow,
  StationerySampleContext,
  StationeryUpdate,
} from "./types";

// All reads use `cache: false` — the admin must see edits/duplicates
// reflected immediately.

export function getStationery(
  id: number
): Promise<CacheAxiosResponse<StationeryRow>> {
  return axios.get<StationeryRow>(`/v6/stationery/${id}`, { cache: false });
}

export function createStationery(
  payload: StationeryCreate
): Promise<AxiosResponse<StationeryRow>> {
  return axios.post<StationeryRow>("/v6/stationery", payload, { cache: false });
}

export function updateStationery(
  id: number,
  patch: StationeryUpdate
): Promise<AxiosResponse<StationeryRow>> {
  return axios.patch<StationeryRow>(`/v6/stationery/${id}`, patch, { cache: false });
}

export function duplicateStationery(
  id: number
): Promise<AxiosResponse<StationeryRow>> {
  return axios.post<StationeryRow>(
    `/v6/stationery/${id}/duplicate`,
    {},
    { cache: false }
  );
}

export function deleteStationery(id: number): Promise<AxiosResponse<void>> {
  return axios.delete<void>(`/v6/stationery/${id}`, { cache: false });
}

export function listStationeryModules(): Promise<
  CacheAxiosResponse<StationeryModule[]>
> {
  return axios.get<StationeryModule[]>("/v6/stationery/modules", { cache: false });
}

// ----- Help links (Links tab) ----------------------------------------------

export function getHelpLinks(
  stationeryId: number
): Promise<CacheAxiosResponse<StationeryHelpLink[]>> {
  return axios.get<StationeryHelpLink[]>(
    `/v6/stationery/${stationeryId}/help-links`,
    { cache: false }
  );
}

export function createHelpLink(
  stationeryId: number,
  body: StationeryHelpLinkCreate
): Promise<AxiosResponse<StationeryHelpLink>> {
  return axios.post<StationeryHelpLink>(
    `/v6/stationery/${stationeryId}/help-links`,
    body,
    { cache: false }
  );
}

export function updateHelpLink(
  stationeryId: number,
  linkId: number,
  body: StationeryHelpLinkUpdate
): Promise<AxiosResponse<StationeryHelpLink>> {
  return axios.put<StationeryHelpLink>(
    `/v6/stationery/${stationeryId}/help-links/${linkId}`,
    body,
    { cache: false }
  );
}

export function deleteHelpLink(
  stationeryId: number,
  linkId: number
): Promise<AxiosResponse<void>> {
  return axios.delete<void>(
    `/v6/stationery/${stationeryId}/help-links/${linkId}`,
    { cache: false }
  );
}

// ----- Field discovery ------------------------------------------------------

/**
 * Field list for a row's template, parsed server-side from the bundled
 * template catalogue (GET /v6/stationery/{id}/fields). Returns the FULL list
 * of referenced context vars (scalars + loop iterables), unlike a render-probe
 * which only surfaces required vars (and these templates default everything).
 *
 * 404 means the row has no v2 template mapped yet, or the template isn't in
 * the catalogue — i.e. the form isn't built. The caller surfaces that as the
 * "not built yet" empty state.
 */
export function getTemplateFields(
  id: number
): Promise<AxiosResponse<StationeryFields>> {
  return axios.get<StationeryFields>(`/v6/stationery/${id}/fields`, {
    cache: false,
    // 404 (not mapped / not built) is handled inline by the Fields tab —
    // don't let the global interceptor fire a red error toast for it.
    suppressErrorToast: true,
  });
}

/**
 * Sample render context for a row's template — used by Preview so the rendered
 * document is populated with example data rather than blank. 404 mirrors the
 * fields endpoint (form not mapped / not built).
 */
export function getSampleContext(
  id: number
): Promise<AxiosResponse<StationerySampleContext>> {
  return axios.get<StationerySampleContext>(
    `/v6/stationery/${id}/sample-context`,
    { cache: false, suppressErrorToast: true }
  );
}

/**
 * Render a template against the v2 stationery service and return an object URL
 * for the result. v2's /render is POST-only with header auth, so previews can
 * no longer be a plain GET navigation — callers open a window synchronously
 * (within the click gesture) then redirect it to this URL once it resolves.
 * Pass a sample `context` (from getSampleContext) to render a populated
 * example; omit it to render with the template's Jinja defaults + branding.
 * Caller revokes the URL.
 */
/**
 * Render a template to an HTML string (not a blob URL) so callers can both
 * embed it (iframe) and inspect it (which field tokens actually appeared).
 * Used by the Fields tab's annotated document preview.
 */
export async function renderTemplateHtml(
  template: string,
  context: Record<string, unknown>
): Promise<string> {
  const resp = await axiosStationery.post(
    "render",
    { template, context, format: "html" },
    {
      responseType: "text",
      transformResponse: [(d) => d],
      suppressErrorToast: true,
    }
  );
  return typeof resp.data === "string" ? resp.data : String(resp.data ?? "");
}

export async function renderTemplatePreviewBlob(
  template: string,
  format: "html" | "pdf" = "html",
  context: Record<string, unknown> = {}
): Promise<Blob> {
  // PDF rendering on v2 needs an explicit Accept header (see OrderPreview's
  // PDF path); HTML is the simple, universally-working path used by the
  // on-screen module previews, so we default to it for the in-tab preview.
  const resp = await axiosStationery.post(
    "render",
    { template, context, format },
    {
      responseType: "blob",
      headers: format === "pdf" ? { Accept: "application/pdf" } : undefined,
    }
  );
  return new Blob([resp.data], {
    type: format === "pdf" ? "application/pdf" : "text/html",
  });
}
